import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Row, Col, Card, List, Input, Button, Badge, Tag, Modal, Form, Select,
  Empty, message, InputNumber, Avatar, Space, Tabs,
} from "antd";
import { PlusOutlined, SendOutlined, FileAddOutlined, CheckOutlined, CloseOutlined, ReloadOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import {
  getConversations, createConversation, getChatMessages, sendChatMessage,
  chatProductionRequest, approveActionRequest, rejectActionRequest, getMentionableUsers,
} from "../api/client";
import { useAuth } from "../context/AuthContext";
import { NotesBoard, Notebook } from "./NotesPage";
import { faNum } from "../utils/faNumberFormat";

const AR_STATUS = { pending: { l: "در انتظارِ تأیید", c: "gold" }, approved: { l: "تأییدشده", c: "green" }, rejected: { l: "ردشده", c: "red" } };
const fa = (n) => faNum(Number(n));

function MessagesPanel() {
  const { user, can } = useAuth();
  const me = user?.username;
  const canApprove = can("production.create");
  const [sp, setSp] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [users, setUsers] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [prOpen, setPrOpen] = useState(false);
  const [form] = Form.useForm();
  const [prForm] = Form.useForm();
  const bottomRef = useRef(null);

  const loadConvs = useCallback(() => getConversations().then((r) => setConvs(r.data)).catch(() => {}), []);
  useEffect(() => { loadConvs(); getMentionableUsers().then((r) => setUsers(r.data)).catch(() => {}); }, [loadConvs]);

  const loadMsgs = useCallback((cid) => {
    if (!cid) return;
    getChatMessages(cid).then((r) => setMsgs(r.data)).catch(() => message.error("خطا در بارگذاریِ پیام‌ها"));
  }, []);

  useEffect(() => { const c = sp.get("c"); if (c) setActive(Number(c)); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!active) return;
    loadMsgs(active);
    const t = setInterval(() => { loadMsgs(active); loadConvs(); }, 12000);
    return () => clearInterval(t);
  }, [active, loadMsgs, loadConvs]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const userOpts = users.filter((u) => u.username !== me)
    .map((u) => ({ value: u.username, label: `${u.full_name} — @${u.username}` }));

  const openConv = (cid) => {
    setActive(cid);
    const next = new URLSearchParams(sp);
    next.set("c", String(cid));
    next.set("tab", "messages");
    // push — «برگشت»ِ مرورگر باید به گفتگوی قبلی برگردد، نه به صفحهٔ قبلیِ منو
    setSp(next);
  };

  const startConv = async () => {
    const v = await form.validateFields();
    const parts = v.participants || [];
    const kind = parts.length > 1 ? "group" : "dm";
    try {
      const r = await createConversation({ kind, participants: parts, title: v.title });
      setNewOpen(false); form.resetFields(); await loadConvs(); openConv(r.data.id);
    } catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
  };

  const send = async () => {
    if (!text.trim() || !active) return;
    const body = text; setText("");
    try { await sendChatMessage(active, body); loadMsgs(active); loadConvs(); }
    catch (e) { message.error(e?.response?.data?.detail || "خطا"); setText(body); }
  };

  const submitPR = async () => {
    const v = await prForm.validateFields();
    try { await chatProductionRequest(active, v); setPrOpen(false); prForm.resetFields(); loadMsgs(active); }
    catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
  };

  const act = async (fn, aid) => {
    try {
      const r = await fn(aid);
      message.success(r.data.order_number ? `پیش‌نویسِ سری ${r.data.order_number} ساخته شد` : "انجام شد");
      loadMsgs(active);
    } catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
  };

  const activeConv = convs.find((c) => c.id === active);

  const renderMsg = (m) => {
    if (m.kind === "system") {
      return <div key={m.id} style={{ textAlign: "center", margin: "8px 0" }}><Tag color="blue">{m.body}</Tag></div>;
    }
    const mine = m.sender === me;
    if (m.kind === "action_request") {
      const ar = m.action_request || {};
      const p = ar.payload || {};
      return (
        <div key={m.id} style={{ display: "flex", justifyContent: mine ? "flex-start" : "flex-end", margin: "6px 0" }}>
          <Card size="small" style={{ maxWidth: 340, borderColor: "#13c2c2" }}
            title={<Space><FileAddOutlined style={{ color: "#13c2c2" }} />درخواستِ دستورِ تولید</Space>}
            extra={<Tag color={AR_STATUS[ar.status]?.c}>{AR_STATUS[ar.status]?.l}</Tag>}>
            <div>محصول: <b>{p.product_type}</b></div>
            <div>تعداد: <b>{fa(p.target_qty)}</b>{p.cut_code ? ` · کدِ برش: ${p.cut_code}` : ""}</div>
            {p.notes && <div style={{ color: "#888" }}>یادداشت: {p.notes}</div>}
            {ar.status === "approved" && ar.result_order_id &&
              <Tag color="green" style={{ marginTop: 6 }}>سفارش ساخته شد ✓</Tag>}
            {ar.status === "pending" && (
              <Space style={{ marginTop: 8 }}>
                {canApprove && <Button size="small" type="primary" icon={<CheckOutlined />}
                  onClick={() => act(approveActionRequest, ar.id)}>تأیید و ساختِ پیش‌نویس</Button>}
                <Button size="small" danger icon={<CloseOutlined />}
                  onClick={() => act(rejectActionRequest, ar.id)}>رد</Button>
              </Space>
            )}
          </Card>
        </div>
      );
    }
    return (
      <div key={m.id} style={{ display: "flex", justifyContent: mine ? "flex-start" : "flex-end", margin: "4px 0" }}>
        <div style={{
          maxWidth: "72%", background: mine ? "var(--brand)" : "var(--bg-subtle,#f0f0f0)",
          color: mine ? "#fff" : "inherit", borderRadius: 12, padding: "7px 12px",
        }}>
          {!mine && activeConv?.kind === "group" && <div style={{ fontSize: 11, opacity: 0.7 }}>{m.sender}</div>}
          <div style={{ whiteSpace: "pre-wrap" }}>{m.body}</div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setNewOpen(true); }}>گفت‌وگوی جدید</Button>
      </div>
      <Row gutter={12}>
        <Col xs={24} md={8}>
          <Card size="small" styles={{ body: { padding: 0, maxHeight: "70vh", overflow: "auto" } }}>
            {convs.length ? (
              <List dataSource={convs} renderItem={(c) => (
                <List.Item
                  style={{ cursor: "pointer", padding: "10px 14px", background: c.id === active ? "var(--bg-subtle,#e6f7ff)" : "transparent" }}
                  onClick={() => openConv(c.id)}>
                  <List.Item.Meta
                    avatar={<Badge count={c.unread} size="small"><Avatar style={{ background: c.kind === "group" ? "#722ed1" : "var(--brand)" }}>{(c.title || "?").charAt(0)}</Avatar></Badge>}
                    title={<span>{c.title} {c.kind === "group" && <Tag>گروه</Tag>}</span>}
                    description={<span style={{ fontSize: 12, color: "#999" }}>{c.last_message || "—"}</span>} />
                </List.Item>
              )} />
            ) : <Empty description="گفت‌وگویی نیست" style={{ padding: 24 }} />}
          </Card>
        </Col>
        <Col xs={24} md={16}>
          <Card
            size="small"
            title={activeConv ? <Space>{activeConv.title}{activeConv.kind === "group" && <Tag color="purple">گروه</Tag>}<ReloadOutlined onClick={() => loadMsgs(active)} /></Space> : "یک گفت‌وگو را باز کنید"}
            styles={{ body: { display: "flex", flexDirection: "column", height: "70vh" } }}>
            {active ? (
              <>
                <div style={{ flex: 1, overflow: "auto", paddingInline: 4 }}>
                  {msgs.map(renderMsg)}
                  <div ref={bottomRef} />
                </div>
                <div style={{ borderTop: "1px solid #eee", paddingTop: 8 }}>
                  <Space.Compact style={{ width: "100%" }}>
                    <Button icon={<FileAddOutlined />} onClick={() => { prForm.resetFields(); setPrOpen(true); }} title="درخواستِ دستورِ تولید" />
                    <Input value={text} onChange={(e) => setText(e.target.value)} onPressEnter={send} placeholder="پیام بنویسید…" />
                    <Button type="primary" icon={<SendOutlined />} onClick={send}>ارسال</Button>
                  </Space.Compact>
                </div>
              </>
            ) : <Empty description="از سمتِ راست یک گفت‌وگو انتخاب کنید یا گفت‌وگوی جدید بسازید" />}
          </Card>
        </Col>
      </Row>

      <Modal title="گفت‌وگوی جدید" open={newOpen} onOk={startConv} onCancel={() => setNewOpen(false)} okText="شروع" cancelText="انصراف" destroyOnHidden>
        <Form form={form} layout="vertical" style={{ direction: "rtl" }}>
          <Form.Item name="participants" label="مخاطب(ها)" rules={[{ required: true, message: "حداقل یک نفر" }]}
            extra="یک نفر = چتِ خصوصی · چند نفر = گروه">
            <Select mode="multiple" showSearch optionFilterProp="label" placeholder="جست‌وجوی کاربر" options={userOpts} />
          </Form.Item>
          <Form.Item name="title" label="نامِ گروه (اختیاری — فقط برای گروه)"><Input /></Form.Item>
        </Form>
      </Modal>

      <Modal title="درخواستِ دستورِ تولید" open={prOpen} onOk={submitPR} onCancel={() => setPrOpen(false)} okText="ارسالِ درخواست" cancelText="انصراف" destroyOnHidden>
        <Form form={prForm} layout="vertical" style={{ direction: "rtl" }}>
          <Form.Item name="product_type" label="نوعِ محصول" rules={[{ required: true }]}><Input placeholder="مثلاً تیشرت نخی" /></Form.Item>
          <Form.Item name="target_qty" label="تعداد" rules={[{ required: true }]}><InputNumber min={1} style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="cut_code" label="کدِ برش (اختیاری)"><Input /></Form.Item>
          <Form.Item name="notes" label="توضیحات (اختیاری)"><Input.TextArea rows={2} /></Form.Item>
        </Form>
        <div style={{ color: "#888", fontSize: 12 }}>پس از تأییدِ مدیرِ تولید، یک سری تولیدِ پیش‌نویس ساخته می‌شود.</div>
      </Modal>
    </div>
  );
}

export default function ChatPage() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") === "notes" ? "notes" : "messages";

  const onTab = (key) => {
    const next = new URLSearchParams(sp);
    next.set("tab", key);
    setSp(next);
  };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>گفتگو</h2>
      <Tabs activeKey={tab} onChange={onTab} items={[
        { key: "messages", label: "پیام‌ها", children: <MessagesPanel /> },
        {
          key: "notes",
          label: "یادداشت‌ها",
          children: (
            <Tabs size="small" items={[
              { key: "board", label: "یادداشت‌های مدیریتی", children: <NotesBoard /> },
              { key: "notebook", label: "دفترچهٔ من", children: <Notebook /> },
            ]} />
          ),
        },
      ]} />
    </div>
  );
}
